// Dummy per-enterprise activity ledger (read-only observation).
//
// CANONICAL SUMMARY SLOT
// The `summary` field on each program is the exact slot the real cached
// AI-generated summary will later land in. Today: pre-written dummy
// string. Tomorrow: a DB-cached LLM summary drops in at the same key
// path with no UI change. The InfoIcon on each program card reads
// program.summary directly.
//
// AGENT NAMES are restricted to the real set on the platform:
//   live capabilities  : Eagle Eye, Shared Inbox, Campaign Mode,
//                        Salesforce Sync, Travel Agent, Recognition Agent,
//                        Ambassador Dashboard, Recurring Programs,
//                        Activity Dashboard, Last-Mile Delivery (where live),
//                        Food Ordering Agent, Gifting Agent
//   planned            : Class Agent, monday.com Sync, Benevity Export,
//                        Last-Mile Delivery (where planned)
// Categories are locked: recurring_activation, recognition, csr,
// fitness_class, travel.

const baseAttendance = (avg, peak, sat, mar) => ({
  avgAttendance: avg,
  peakAttendance: peak,
  satisfactionScore: sat,
  monthlyActiveRate: mar,
});

const DUMMY_PROGRAMS_BY_SLUG = {
  acme: [
    {
      id: 'acme-sweet-treat',
      name: 'Sweet Treat Tuesday',
      category: 'recurring_activation',
      status: 'active',
      cadence: 'Weekly (Tuesday)',
      reach: { offices: 2, employees: 1240 },
      startedAt: 'Jan 2025',
      summary:
        'Weekly office-wide sweet delivery aimed at a midweek community moment. Coordinated across HQ + Seattle through the food ordering agent with local last-mile delivery.',
      agents: [
        { name: 'Food Ordering Agent', role: 'Vendor selection + order placement', status: 'live' },
        { name: 'Last-Mile Delivery', role: 'Per-site dispatch', status: 'live' },
        { name: 'Eagle Eye', role: 'Local vendor sourcing', status: 'live' },
        { name: 'Recurring Programs', role: 'Cadence + intake', status: 'live' },
      ],
      attendance: baseAttendance(38, 64, 4.7, '54%'),
      history: [
        {
          id: 'j-1',
          when: 'May 26, 2026',
          kind: 'activation',
          status: 'completed',
          notes: 'Both offices participated; 41 treats HQ, 22 Seattle. One late delivery rerouted to backup vendor.',
        },
        {
          id: 'j-2',
          when: 'May 19, 2026',
          kind: 'activation',
          status: 'completed',
          notes: '42 HQ, 24 Seattle. Vendor Sweet Pea Bakery on-time at both sites.',
        },
        {
          id: 'j-3',
          when: 'May 12, 2026',
          kind: 'activation',
          status: 'completed',
          notes: '36 HQ, 19 Seattle. Lower turnout, May long weekend before.',
        },
      ],
    },
    {
      id: 'acme-team-lunch',
      name: 'Team Lunch Thursday',
      category: 'recurring_activation',
      status: 'active',
      cadence: 'Weekly (Thursday)',
      reach: { offices: 2, employees: 1240 },
      startedAt: 'Feb 2025',
      summary:
        'Weekly catered team lunch run by the food ordering agent. Drives in-office Thursday density; vendor rotation managed by Eagle Eye.',
      agents: [
        { name: 'Food Ordering Agent', role: 'Vendor selection + order placement', status: 'live' },
        { name: 'Last-Mile Delivery', role: 'Per-site dispatch', status: 'live' },
        { name: 'Eagle Eye', role: 'Vendor rotation + sourcing', status: 'live' },
        { name: 'Recurring Programs', role: 'Cadence + intake form', status: 'live' },
      ],
      attendance: baseAttendance(80, 120, 4.6, '72%'),
      history: [
        {
          id: 'j-1',
          when: 'May 28, 2026',
          kind: 'activation',
          status: 'completed',
          notes: 'Caterer Pita Pal, 96 lunches HQ + 24 Seattle. Two dietary-restriction mismatches caught by intake form.',
        },
        {
          id: 'j-2',
          when: 'May 21, 2026',
          kind: 'activation',
          status: 'completed',
          notes: '88 HQ + 21 Seattle. Vendor Green Bowl, clean run.',
        },
        {
          id: 'j-3',
          when: 'May 14, 2026',
          kind: 'activation',
          status: 'completed',
          notes: '92 HQ + 26 Seattle. One missing allergen tag, vendor flagged for follow-up.',
        },
      ],
    },
    {
      id: 'acme-gifting',
      name: 'Birthday & Work-Anniversary Gifting',
      category: 'recognition',
      status: 'active',
      cadence: 'Continuous',
      reach: { offices: 2, employees: 1240 },
      startedAt: 'Mar 2025',
      summary:
        'Ongoing milestone gifting program. Every birthday and work-anniversary triggers a curated gift via the gifting agent with home-address delivery.',
      agents: [
        { name: 'Gifting Agent', role: 'Gift selection + personalization', status: 'live' },
        { name: 'Last-Mile Delivery', role: 'Home-address dispatch', status: 'live' },
      ],
      attendance: baseAttendance(18, 22, 4.8, '100%'),
      history: [
        {
          id: 'j-1',
          when: 'May 27, 2026',
          kind: 'gifting',
          status: 'completed',
          notes: '7 anniversaries this week; 3 x 5-year, 2 x 10-year, 2 x 1-year. All deliveries confirmed.',
        },
        {
          id: 'j-2',
          when: 'May 20, 2026',
          kind: 'gifting',
          status: 'completed',
          notes: '4 birthdays, 2 anniversaries. One redelivery requested (recipient on PTO).',
        },
        {
          id: 'j-3',
          when: 'May 13, 2026',
          kind: 'gifting',
          status: 'completed',
          notes: '9 events. All delivered same-day or next-day.',
        },
      ],
    },
    {
      id: 'acme-impact-day',
      name: 'Impact Day 2026',
      category: 'csr',
      status: 'active',
      cadence: 'Annual',
      reach: { offices: 2, employees: 1240 },
      startedAt: 'Apr 2025',
      summary:
        'Annual company-wide day of service. Campaign Mode coordinates 14 nonprofit partners across HQ + Seattle; ambassadors tracked per-office.',
      agents: [
        { name: 'Campaign Mode', role: 'Multi-site activation', status: 'live' },
        { name: 'Eagle Eye', role: 'Nonprofit partner sourcing', status: 'live' },
        { name: 'Ambassador Dashboard', role: 'Per-office ambassador tracking', status: 'live' },
        { name: 'Shared Inbox', role: 'Partner correspondence', status: 'live' },
      ],
      attendance: baseAttendance(380, 590, 4.8, '42%'),
      history: [
        {
          id: 'j-1',
          when: 'Apr 17, 2026',
          kind: 'activation',
          status: 'completed',
          notes: '312 employees logged volunteer hours across 14 nonprofit partners. Benevity export pending Q2 close.',
        },
        {
          id: 'j-2',
          when: 'Apr 18, 2025',
          kind: 'activation',
          status: 'completed',
          notes: 'First-year run; 247 participants, 9 partners. Used as the baseline for 2026 planning.',
        },
      ],
    },
    {
      id: 'acme-offsite',
      name: 'Annual Team Offsite (Whistler)',
      category: 'travel',
      status: 'planning',
      cadence: 'Annual',
      reach: { offices: 1, employees: 240 },
      startedAt: 'Pending launch',
      summary:
        'Three-day company offsite at Whistler. Travel Agent coordinates lodging, flights, and credit-auth forms for invited attendees. Currently in planning.',
      agents: [
        { name: 'Travel Agent', role: 'Lodging + flights + credit auth', status: 'live' },
      ],
      attendance: baseAttendance(0, 0, null, '0%'),
      history: [],
    },
    {
      id: 'acme-yoga',
      name: 'Yoga Flow Wednesdays',
      category: 'fitness_class',
      status: 'active',
      cadence: 'Weekly (Wednesday lunch)',
      reach: { offices: 2, employees: 1240 },
      startedAt: 'Jan 2025',
      summary:
        'Weekly drop-in yoga class. Run by a rotating instructor pool sourced through Eagle Eye; cadence + RSVP via Recurring Programs.',
      agents: [
        { name: 'Recurring Programs', role: 'Cadence + RSVP', status: 'live' },
        { name: 'Activity Dashboard', role: 'Per-office engagement visibility', status: 'live' },
        { name: 'Eagle Eye', role: 'Instructor sourcing', status: 'live' },
        { name: 'Class Agent', role: 'Per-class engagement + safety scoring', status: 'planned' },
      ],
      attendance: baseAttendance(44, 70, 4.9, '68%'),
      history: [
        { id: 'j-1', when: 'May 27, 2026', kind: 'class', status: 'completed', notes: '52 HQ, 18 Seattle. Instructor Alyssa T.' },
        { id: 'j-2', when: 'May 20, 2026', kind: 'class', status: 'completed', notes: '47 HQ, 16 Seattle.' },
        { id: 'j-3', when: 'May 13, 2026', kind: 'class', status: 'completed', notes: 'Instructor swap mid-day; 40 attended.' },
        { id: 'j-4', when: 'May 6, 2026', kind: 'class', status: 'completed', notes: '49 HQ, 21 Seattle.' },
      ],
    },
  ],

  'wbd-wellbeing': [
    {
      id: 'wbd-sweet-treat',
      name: 'Sweet Treat Tuesday',
      category: 'recurring_activation',
      status: 'active',
      cadence: 'Monthly (last Tuesday)',
      reach: { offices: 20, employees: 1600 },
      startedAt: 'Jan 2025',
      summary:
        'Recurring office-wide sweet delivery aimed at in-office community moments. Coordinated across 20 sites; last-mile is still being onboarded.',
      agents: [
        { name: 'Food Ordering Agent', role: 'Vendor selection + order placement', status: 'live' },
        { name: 'Recurring Programs', role: 'Cadence + RSVP', status: 'live' },
        { name: 'Eagle Eye', role: 'Local vendor sourcing', status: 'live' },
        { name: 'Last-Mile Delivery', role: 'Per-site dispatch', status: 'planned' },
      ],
      attendance: baseAttendance(38, 64, 4.7, '54%'),
      history: [
        { id: 'j-1', when: 'Apr 28, 2026', kind: 'activation', status: 'completed', notes: '18 of 20 offices participated.' },
        { id: 'j-2', when: 'Mar 24, 2026', kind: 'activation', status: 'completed', notes: 'All 20 offices.' },
        { id: 'j-3', when: 'Feb 24, 2026', kind: 'activation', status: 'completed', notes: 'All 20 offices; one vendor mismatch resolved on-call.' },
      ],
    },
    {
      id: 'wbd-mindful',
      name: 'Mindful Mornings',
      category: 'fitness_class',
      status: 'active',
      cadence: 'Weekly (Friday morning)',
      reach: { offices: 100, employees: 8000 },
      startedAt: 'Feb 2025',
      summary:
        'Short guided mindfulness session every Friday morning. Cadence and per-office visibility tracked; class agent will take over engagement scoring when live.',
      agents: [
        { name: 'Recurring Programs', role: 'Cadence + RSVP', status: 'live' },
        { name: 'Activity Dashboard', role: 'Cross-office visibility', status: 'live' },
        { name: 'Class Agent', role: 'Per-class engagement scoring', status: 'planned' },
      ],
      attendance: baseAttendance(22, 41, 4.5, '48%'),
      history: [
        { id: 'j-1', when: 'May 22, 2026', kind: 'class', status: 'completed', notes: '24 attendees across 8 offices.' },
        { id: 'j-2', when: 'May 15, 2026', kind: 'class', status: 'completed', notes: '36 attendees, peak office NYC.' },
        { id: 'j-3', when: 'May 8, 2026', kind: 'class', status: 'completed', notes: '19 attendees, holiday week dip.' },
      ],
    },
    {
      id: 'wbd-volunteer-hours',
      name: 'Quarterly Volunteer Hours',
      category: 'csr',
      status: 'active',
      cadence: 'Quarterly',
      reach: { offices: 100, employees: 8000 },
      startedAt: 'Q2 2024',
      summary:
        'Quarterly company-wide volunteer drive. Nonprofit partners sourced via Eagle Eye; ambassadors tracked per office; campaign coordinated across 100+ sites.',
      agents: [
        { name: 'Campaign Mode', role: 'Multi-site activation', status: 'live' },
        { name: 'Eagle Eye', role: 'Nonprofit discovery', status: 'live' },
        { name: 'Ambassador Dashboard', role: 'Per-office tracking', status: 'live' },
      ],
      attendance: baseAttendance(180, 240, 4.7, '38%'),
      history: [
        { id: 'j-1', when: 'Apr 5, 2026', kind: 'activation', status: 'completed', notes: 'Q1 drive, 287 partners, 92 sites participating.' },
        { id: 'j-2', when: 'Jan 6, 2026', kind: 'activation', status: 'completed', notes: 'Year-open drive, 211 partners.' },
      ],
    },
  ],

  instacart: [
    {
      id: 'instacart-impact',
      name: 'Local Impact Days',
      category: 'csr',
      status: 'active',
      cadence: 'Quarterly',
      reach: { offices: 6, employees: 0 },
      startedAt: 'Mar 2025',
      summary:
        'Quarterly nonprofit-partner activation across non-office markets. Eagle Eye sources partners; Shared Inbox handles outreach; Salesforce keeps the partner pool in sync.',
      agents: [
        { name: 'Eagle Eye', role: 'Nonprofit discovery', status: 'live' },
        { name: 'Campaign Mode', role: 'Multi-market activation', status: 'live' },
        { name: 'Shared Inbox', role: 'Partner outreach', status: 'live' },
        { name: 'Salesforce Sync', role: 'CRM partner pool', status: 'live' },
      ],
      attendance: baseAttendance(120, 180, 4.6, '42%'),
      history: [
        { id: 'j-1', when: 'Apr 18, 2026', kind: 'activation', status: 'completed', notes: '12 nonprofit partners across 6 markets.' },
        { id: 'j-2', when: 'Jan 24, 2026', kind: 'activation', status: 'completed', notes: '11 partners; one outreach blocked, recovered via backup contact.' },
      ],
    },
    {
      id: 'instacart-tenure-gifting',
      name: 'Tenure Milestone Gifting',
      category: 'recognition',
      status: 'active',
      cadence: 'Continuous (per milestone)',
      reach: { offices: 6, employees: 0 },
      startedAt: 'Aug 2025',
      summary:
        'Ongoing tenure-milestone program. Gifting agent selects a milestone gift per employee; last-mile handles direct-to-home delivery for a remote-first workforce.',
      agents: [
        { name: 'Gifting Agent', role: 'Milestone gift selection', status: 'live' },
        { name: 'Last-Mile Delivery', role: 'Home-address dispatch', status: 'live' },
      ],
      attendance: baseAttendance(96, 140, 4.7, '100%'),
      history: [
        { id: 'j-1', when: 'May 26, 2026', kind: 'gifting', status: 'completed', notes: '11 milestones; 4 x 1-year, 5 x 3-year, 2 x 5-year.' },
        { id: 'j-2', when: 'May 19, 2026', kind: 'gifting', status: 'completed', notes: '8 milestones, all delivered.' },
        { id: 'j-3', when: 'May 12, 2026', kind: 'gifting', status: 'completed', notes: '14 milestones; one redelivery (incorrect address on file).' },
      ],
    },
    {
      id: 'instacart-offsite',
      name: 'All-Company Offsite',
      category: 'travel',
      status: 'planning',
      cadence: 'Annual',
      reach: { offices: 1, employees: 0 },
      startedAt: 'Pending launch',
      summary:
        'Annual all-company offsite for the remote-first workforce. Travel Agent coordinates inbound flights, lodging, and credit-auth forms.',
      agents: [
        { name: 'Travel Agent', role: 'Lodging + flights + credit auth', status: 'live' },
      ],
      attendance: baseAttendance(0, 0, null, '0%'),
      history: [],
    },
  ],
};

const DEFAULT_PROGRAMS = [
  {
    id: 'default-program',
    name: 'Sample Program',
    category: 'recognition',
    status: 'planning',
    cadence: 'TBD',
    reach: { offices: 0, employees: 0 },
    startedAt: 'Not yet observed',
    summary:
      'Placeholder. Once this enterprise runs a real program, it will appear here for observation.',
    agents: [],
    attendance: baseAttendance(0, 0, null, '0%'),
    history: [],
  },
];

export function programsForSlug(slug) {
  if (!slug) return DEFAULT_PROGRAMS;
  return DUMMY_PROGRAMS_BY_SLUG[slug] || DEFAULT_PROGRAMS;
}

export { DUMMY_PROGRAMS_BY_SLUG, DEFAULT_PROGRAMS };
